import React from 'react';
import { Heart, Trash2, ShoppingBag, Gift } from 'lucide-react';
import { Card } from '../components/ui/card';

const CommunityService = () => {
  const events = [
    {
      icon: Trash2,
      title: 'Neighborhood Clean & Lead',
      description: 'Neighborhood Clean & Lead is a hands-on community improvement initiative where youth take the lead in restoring, beautifying, and uplifting local neighborhoods. Participants work together to remove litter, refresh community spaces, and promote environmental responsibility. Beyond physical cleanup, students learn leadership, teamwork, and ownership—proving that positive change starts with them. This event empowers youth to become visible leaders and role models within their own communities.',
      image: 'https://customer-assets.emergentagent.com/job_why-me-org/artifacts/mdkmyo0b_RUNWME.jpg'
    },
    {
      icon: ShoppingBag,
      title: 'Feed the City Food Drive',
      description: 'The Feed the City Food Drive brings volunteers together to fight food insecurity by collecting, sorting, and distributing meal kits and pantry staples to families in need. Through collaboration with local shelters, food banks, and community partners, students witness firsthand the impact of service and compassion. This event teaches empathy, organization, and civic responsibility while ensuring that no family in our community goes hungry.',
      image: 'https://customer-assets.emergentagent.com/job_why-me-org/artifacts/8z4wu2lr_WHYME%20BKFST.jpg'
    },
    {
      icon: Gift,
      title: 'Beau Strong Toy Drive at Texas Children\'s Hospital',
      description: 'The Why ME Foundation Toy Drive spreads joy during the holiday season by providing new toys, books, and gifts to children from under-resourced families. Youth volunteers help organize donations, wrap gifts, and create a warm, festive experience for recipients. This event encourages generosity, gratitude, and community unity—reminding young leaders that small acts of kindness can create lifelong memories.',
      image: 'https://customer-assets.emergentagent.com/job_foundation-web-5/artifacts/vkbu5f6n_IMG_4376.png'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-black text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#E6B933] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#E6B933] rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-block mb-6 px-6 py-2 bg-[#E6B933]/20 rounded-full border border-[#E6B933]">
              <p className="text-[#E6B933] font-semibold">Community Impact</p>
            </div>
            <h1 className="text-6xl md:text-7xl font-bold mb-6">
              Community <span className="text-[#E6B933]">Service Events</span>
            </h1>
            <p className="text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Making a difference through hands-on service, leadership, and compassion
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Service That <span className="text-[#E6B933]">Transforms</span>
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Our community service events empower youth to become visible leaders while making tangible improvements in their neighborhoods. Through hands-on projects like neighborhood cleanups, food drives, and toy distributions, students develop empathy, teamwork, and civic responsibility.
            </p>
          </div>
        </div>
      </section>

      {/* Events */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our <span className="text-[#E6B933]">Events</span>
            </h2>
            <p className="text-xl text-gray-600">Community-driven initiatives that create lasting impact</p>
          </div>

          <div className="space-y-16">
            {events.map((event, index) => {
              const IconComponent = event.icon;
              return (
                <div key={index} className={`grid md:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                  <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-16 h-16 bg-[#E6B933]/10 rounded-xl flex items-center justify-center">
                        <IconComponent className="text-[#E6B933]" size={32} />
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900">{event.title}</h3>
                    </div>
                    <p className="text-lg text-gray-600 leading-relaxed">{event.description}</p>
                  </div>
                  <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                    <Card className="overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300">
                      <img 
                        src={event.image} 
                        alt={event.title}
                        className="w-full h-80 object-cover"
                      />
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Impact Statement */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-12 bg-gradient-to-br from-gray-50 to-white border-2 border-[#E6B933]">
            <div className="text-center">
              <Heart className="w-16 h-16 text-[#E6B933] mx-auto mb-6" />
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Building <span className="text-[#E6B933]">Tomorrow's Leaders</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Through community service, our youth learn that positive change starts with them. Every event is an opportunity to lead, serve, and inspire others to make a difference.
              </p>
            </div>
          </Card>
        </div>
      </section>

      {/* Holiday Spirit Photo */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="https://customer-assets.emergentagent.com/job_foundation-web-5/artifacts/7bn7ciij_IMG_0237.png" 
              alt="WHY ME Foundation team spreading holiday cheer" 
              className="w-full h-auto object-cover"
            />
          </div>
          <p className="text-center text-gray-600 mt-4 text-lg">Our team spreading holiday joy in the community</p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#E6B933] rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-5xl font-bold mb-6">
            Get <span className="text-[#E6B933]">Involved</span>
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Want to participate in our community service events or learn more? Contact us today.
          </p>
          <a href="mailto:thewhymefoundation@gmail.com?subject=Community Service Event Inquiry">
            <button className="bg-[#E6B933] hover:bg-[#D4A01F] text-black font-bold px-8 py-4 rounded-lg text-lg transition-all duration-300 hover:scale-105">
              Contact Us About Events
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default CommunityService;
